import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Formik } from 'formik';
import { Form, Input } from 'formik-antd';
import { Row, Col } from 'antd';
import * as Yup from 'yup';
import cx from 'classnames';

import FormItem from 'components/formik/FormItem';
import BaseAlert from 'components/wrappers/BaseAlert';
import SubmitButton from 'components/formik/SubmitButton';
import { login } from 'redux/reducers/authenticationSlice';
import { ApplicationTypeEnum, TokenResponse } from 'api/accountApiClient';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { isAdminRole } from 'utils/userRolesUtils';
import { BackofficeUserModel } from 'api/backofficeApiClient';
import MultiFactorAuthSection from './MultiFactorAuthSection';

import logo from 'assets/img/logo-dark.svg';

import style from '../AuthorizationPage.module.less';

interface FormValues {
  email: string;
  password: string;
}

const requiredMessage = 'Field is required';

const validationSchema = Yup.object({
  email: Yup.string()
    .required(requiredMessage)
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum'),
  password: Yup.string()
    .required(requiredMessage)
    .max(50, 'Password should be 50 characters maximum'),
});

const initialValues = { email: '', password: '' };

export default function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const uid = location.state?.uid;

  const handleSubmit = async (values: FormValues) => {
    const result = await dispatch(
      login({
        ...values,
        applicationType: ApplicationTypeEnum.Backoffice,
      }),
    );

    if (login.fulfilled.match(result)) {
      const uid = (result.payload as TokenResponse)?.authCode;
      // go to the page to enter the 2FA code
      if (uid)
        return navigate('/login', {
          replace: true,
          state: { uid },
        });

      // redirect to the 1 - location.state (requested URL) OR
      // 2 - to the / home page when account settings progress tracker is completed OR isAdmin
      // 3 - to the /company-profile when account settings progress tracker is uncompleted
      const user = result.payload as BackofficeUserModel;

      const getCorrectPathname = () => {
        if (isAdminRole({ user })) {
          return user.isBOTwoFactorAuthStatus ? '/' : '/two-fa-activation';
        }
        return user.isAccountSettingsComplete ? '/' : '/company-profile';
      };

      const pathname = getCorrectPathname();

      const { from } = (location.state as any) || { from: { pathname } };

      navigate(from, { replace: true });
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <div className={cx('scroll-box', style.authPage)}>
      <Row justify="end" className={style.fullHeight}>
        <Col xl={14}>
          <div className={style.authPageBg}></div>
        </Col>
        <Col xs={24} xl={10}>
          {uid && <MultiFactorAuthSection uid={uid} />}
          {!uid && (
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
            >
              <Form
                className={cx(style.authPageForm, style.signInForm)}
                layout="vertical"
              >
                <div className={style.authPageTitle}>
                  <div>Log in</div>
                  <div className={style.authPageLogo}>
                    <img src={logo} alt="myBasePay" />
                  </div>
                </div>

                <div className={style.authPageContent}>
                  <FormItem
                    data-id="signInEmailField"
                    name="email"
                    label="Email address"
                  >
                    <Input
                      size="large"
                      name="email"
                      autoComplete="off"
                      suffix={<></>}
                    />
                  </FormItem>

                  <FormItem
                    data-id="signInPasswordField"
                    name="password"
                    label="Password"
                  >
                    <Input
                      size="large"
                      name="password"
                      type="password"
                      autoComplete="off"
                      suffix={<></>}
                    />
                  </FormItem>

                  <Row gutter={20} align="middle" className={style.submitBox}>
                    <Col
                      xs={{ span: 24, order: 2 }}
                      md={{ span: 10, order: 0 }}
                    >
                      <SubmitButton
                        data-id="loginBtn"
                        type="primary"
                        size="large"
                        block
                      >
                        Log in
                      </SubmitButton>
                    </Col>
                    <Col xs={24} md={14}>
                      <div className={style.authPagePass}>
                        <Link
                          data-id="forgotPasswordLink"
                          to="/forgot-password"
                        >
                          Forgot password?
                        </Link>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Form>
            </Formik>
          )}
        </Col>
      </Row>
    </div>
  );
}
